/**
 * Claims Measures tab: 479/484 entry with ALL FOUR estimation modes
 * (cms_official_points / prior_year_params / assumed_params / direct_points)
 * + the below-case-minimum flag.
 *
 * Redesigned 2026-07-14 PM (owner: "make it more guiding — play with colors"):
 * numbered steps, mode selection as color-accented radio CARDS, and a result
 * panel using the D10 badge colors (COMPUTED emerald / PROJECTED blue /
 * AWAITING BMK amber).
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { CircleDot, Circle, CheckCircle2, CircleDashed, ChevronDown, ChevronRight } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { logicForYear } from '@/lib/app-plus/years/registry';
import type { AdminClaimsEntry } from '@/lib/app-plus-scoring';
import type { UseAppPlusScoring, StoredSubmission } from '../lib/useAppPlusScoring';

const MODES: Array<{
  value: AdminClaimsEntry['mode']; label: string; description: string; badge: string; badgeClass: string;
}> = [
  { value: 'cms_official_points', label: 'CMS official points',
    description: 'CMS\u2019s report arrived — enter the points it shows. This is the settled number.',
    badge: 'COMPUTED', badgeClass: 'border-emerald-300 text-emerald-700 bg-emerald-50' },
  { value: 'prior_year_params', label: 'Prior-year benchmark',
    description: 'Score this year\u2019s measured rate on last year\u2019s CMS ladder (median/SD).',
    badge: 'PROJECTED', badgeClass: 'border-blue-300 text-blue-700 bg-blue-50' },
  { value: 'assumed_params', label: 'Assumed benchmark',
    description: 'You provide the median/SD assumption AND your measured rate.',
    badge: 'PROJECTED', badgeClass: 'border-blue-300 text-blue-700 bg-blue-50' },
  { value: 'direct_points', label: 'Assume points directly',
    description: 'Skip the math — assume a points value (e.g. \u201cassume median \u2192 7.5\u201d).',
    badge: 'PROJECTED', badgeClass: 'border-blue-300 text-blue-700 bg-blue-50' },
];

function StepLabel({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">{n}</span>
      <span className="text-sm font-semibold">{children}</span>
    </div>
  );
}

function draftFromStored(id: '479' | '484', stored?: StoredSubmission): AdminClaimsEntry {
  const e = stored?.payload?.adminEntry;
  if (e && e.measureId === id) return { ...e };
  return { measureId: id, mode: id === '484' ? 'direct_points' : 'prior_year_params' };
}

export function ClaimsMeasuresTab({ scoring }: { scoring: UseAppPlusScoring }) {
  const { programYear, isCurrentYearLocked } = useProgramYear();
  const { toast } = useToast();
  // D13: resolve through the year's logic module.
  const logic = useMemo(() => logicForYear(programYear), [programYear]);
  const adminConfig = useMemo(() => logic.getAdminClaimsConfig(programYear), [logic, programYear]);

  const [drafts, setDrafts] = useState<Record<'479' | '484', AdminClaimsEntry>>({
    479: draftFromStored('479'), 484: draftFromStored('484'),
  });
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState<Record<'479' | '484', boolean>>({ 479: false, 484: false });

  /** "Completed" = a source is chosen AND its required values are entered (or the edge case is flagged). */
  const isComplete = (d: AdminClaimsEntry): boolean => {
    if (d.belowCaseMinimum) return true;
    switch (d.mode) {
      case 'cms_official_points': return d.officialPoints != null;
      case 'prior_year_params': return d.ratePct != null;
      case 'assumed_params': return d.ratePct != null && d.assumedMedian != null && d.assumedSd != null;
      case 'direct_points': return d.directPoints != null;
      default: return false;
    }
  };

  useEffect(() => {
    setDrafts({
      479: draftFromStored('479', scoring.entries['479']),
      484: draftFromStored('484', scoring.entries['484']),
    });
  }, [scoring.entries]);

  const save = async () => {
    setSaving(true);
    try {
      const rows: StoredSubmission[] = (['479', '484'] as const).map(id => ({
        measure_id: id,
        collection_type: 'CMS Administrative Claims',
        reported: true,
        payload: { adminEntry: drafts[id] },
      }));
      await scoring.save(rows);
      toast({ title: 'Claims measures saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const renderMeasure = (id: '479' | '484', title: string) => {
    const d = drafts[id];
    const params = adminConfig.measures[id];
    const result = (() => { try { return logic.scoreAdminClaims(d, adminConfig); } catch { return null; } })();
    const set = (patch: Partial<AdminClaimsEntry>) =>
      setDrafts(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));

    const statusBadge = result && (
      <Badge variant="outline" className={cn(
        result.displayState === 'official' || result.displayState === 'final'
          ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
          : result.displayState === 'unknowable'
            ? 'border-amber-400 text-amber-700 bg-amber-50'
            : 'border-blue-300 text-blue-700 bg-blue-50')}>
        {result.displayState === 'official' || result.displayState === 'final' ? 'COMPUTED'
          : result.displayState === 'unknowable' ? 'AWAITING BMK' : 'PROJECTED'}
      </Badge>
    );

    const complete = isComplete(d);
    const isOpen = open[id];

    return (
      <Card key={id}>
        {/* Expandable header with a clear done/pending indicator (owner 2026-07-14 PM) */}
        <Collapsible open={isOpen} onOpenChange={(o) => setOpen(prev => ({ ...prev, [id]: o }))}>
        <CollapsibleTrigger asChild>
        <CardHeader className="flex-row items-center justify-between space-y-0 cursor-pointer hover:bg-muted/30 transition-colors rounded-t-lg">
          <div className="flex items-start gap-3">
            {isOpen ? <ChevronDown className="mt-1 h-4 w-4 text-slate-500 shrink-0" /> : <ChevronRight className="mt-1 h-4 w-4 text-slate-500 shrink-0" />}
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                {id} — {title}
                {complete ? (
                  <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">
                    <CheckCircle2 className="mr-1 h-3 w-3" /> Completed
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50">
                    <CircleDashed className="mr-1 h-3 w-3" /> Pending setup
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                CMS calculates this from claims — the ACO submits nothing. Inverse (lower is better) · case minimum {params.caseMinimum}.
                {!complete && <> Expand and pick how to <span className="font-medium text-foreground">estimate</span> it.</>}
              </CardDescription>
            </div>
          </div>
          {result && (
            <div className="rounded-lg border bg-muted/30 px-4 py-2 text-center shrink-0">
              <div className="text-3xl font-bold tabular-nums">{result.points.toFixed(1)}</div>
              {statusBadge}
            </div>
          )}
        </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
        <CardContent className="space-y-4 pt-0">
          {/* Step 1 — source */}
          <div className="space-y-2">
            <StepLabel n={1}>Where does the number come from?</StepLabel>
            <div className="grid gap-2 sm:grid-cols-2">
              {MODES.map(mode => {
                const active = d.mode === mode.value;
                return (
                  <button key={mode.value} disabled={isCurrentYearLocked}
                    onClick={() => set({ mode: mode.value })}
                    className={cn(
                      'flex items-start gap-2.5 rounded-lg border p-3 text-left transition-all',
                      active
                        ? 'border-primary ring-1 ring-primary/40 bg-primary/5'
                        : 'border-border bg-background hover:bg-accent/40')}>
                    {active
                      ? <CircleDot className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
                    <span>
                      <span className="flex items-center gap-2 text-sm font-medium">
                        {mode.label}
                        <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', mode.badgeClass)}>{mode.badge}</Badge>
                      </span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{mode.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            {id === '484' && d.mode === 'prior_year_params' && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                484 has NO prior year (new to the program in 2026) — this mode renders AWAITING BMK until CMS posts parameters.
              </p>
            )}
          </div>

          {/* Step 2 — values */}
          <div className="space-y-2">
            <StepLabel n={2}>Enter the values</StepLabel>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {d.mode === 'cms_official_points' && (
              <div className="space-y-1.5">
                <Label>Official points (0-10)</Label>
                <Input type="number" min={0} max={10} step="0.1" disabled={isCurrentYearLocked}
                  value={d.officialPoints ?? ''}
                  onChange={e => set({ officialPoints: e.target.value === '' ? undefined : Number(e.target.value) })} />
              </div>
            )}
            {(d.mode === 'prior_year_params' || d.mode === 'assumed_params') && (
              <div className="space-y-1.5">
                <Label>Measured rate %</Label>
                <Input type="number" min={0} step="0.01" disabled={isCurrentYearLocked}
                  value={d.ratePct ?? ''}
                  onChange={e => set({ ratePct: e.target.value === '' ? undefined : Number(e.target.value) })} />
              </div>
            )}
            {d.mode === 'assumed_params' && (
              <>
                <div className="space-y-1.5">
                  <Label>Assumed median %</Label>
                  <Input type="number" min={0} step="0.01" disabled={isCurrentYearLocked}
                    value={d.assumedMedian ?? ''}
                    onChange={e => set({ assumedMedian: e.target.value === '' ? undefined : Number(e.target.value) })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Assumed SD</Label>
                  <Input type="number" min={0} step="0.01" disabled={isCurrentYearLocked}
                    value={d.assumedSd ?? ''}
                    onChange={e => set({ assumedSd: e.target.value === '' ? undefined : Number(e.target.value) })} />
                </div>
              </>
            )}
            {d.mode === 'direct_points' && (
              <div className="space-y-1.5">
                <Label>Assumed points (default median = {adminConfig.medianAnchorPoints})</Label>
                <Input type="number" min={0} max={10} step="0.1" disabled={isCurrentYearLocked}
                  value={d.directPoints ?? ''}
                  placeholder={String(adminConfig.medianAnchorPoints)}
                  onChange={e => set({ directPoints: e.target.value === '' ? undefined : Number(e.target.value) })} />
              </div>
            )}
          </div>
          </div>

          {/* Edge case */}
          <div className="flex items-center justify-between rounded-lg border border-dashed p-3">
            <Label className="text-sm text-muted-foreground">
              CMS didn't score this measure (below its case minimum) — exclude it; the floor is NOT armed
            </Label>
            <Switch checked={!!d.belowCaseMinimum} disabled={isCurrentYearLocked}
              onCheckedChange={v => set({ belowCaseMinimum: v })} />
          </div>

          {/* Step 3 — result */}
          {result && (
            <div className={cn('flex items-center justify-between rounded-lg border px-3 py-2',
              result.displayState === 'unknowable' ? 'border-amber-200 bg-amber-50/60'
                : result.displayState === 'official' || result.displayState === 'final'
                  ? 'border-emerald-200 bg-emerald-50/60' : 'border-blue-200 bg-blue-50/60')}>
              <div className="flex items-center gap-2">
                <StepLabel n={3}>Result</StepLabel>
                {statusBadge}
              </div>
              <p className="text-xs text-muted-foreground max-w-[60%] text-right">{result.detail}</p>
            </div>
          )}
        </CardContent>
        </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      {renderMeasure('479', 'Hospital-Wide, 30-Day, All-Cause Unplanned Readmission Rate')}
      {renderMeasure('484', 'Risk-standardized Hospital Admission Rates (Multiple Chronic Conditions)')}
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving || isCurrentYearLocked}>
          {saving ? 'Saving…' : 'Save claims measures'}
        </Button>
      </div>
    </div>
  );
}
